/**
 * Leads hooks with optimistic rollback + CRM/webhook export + relance stubs.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApiClient } from '../context';
import type { ApiError } from '../hc';
import { deleteJson, getJson, patchJson, postJson } from './_http';
import { useNotImplemented, useNotImplementedQuery } from './_stub';

/* ─── Leads — Wave 5d Agent 5d.2 (FULLY IMPLEMENTED) ───────────────────── */

/** Mirror of `leads` table row (Drizzle infer). Kept inline so the
 *  api-client doesn't depend on `@barel/db`. */
export interface LeadRow {
  id: string;
  userId: string;
  linkedinAccountId: string | null;
  businessId: string | null;
  conversationId: string | null;
  prospectName: string;
  prospectInitials: string;
  prospectColor: string | null;
  prospectTitle: string | null;
  prospectCompany: string | null;
  prospectLinkedinUrn: string | null;
  prospectVanitySlug: string | null;
  prospectAvatarUrl: string | null;
  sourcePill: 'Waalaxy' | 'LinkedIn' | 'Internal' | 'CSV' | 'Manual' | 'Unknown';
  sourceDetected: boolean | null;
  validatedFrom: 'inbox' | 'slack' | 'discord' | 'extension';
  temperature: 'cold' | 'warm' | 'hot';
  isVeryHot: boolean | null;
  status: 'rdv_pris' | 'rdv_passe' | 'client_pot' | 'client' | 'a_relancer' | 'perdu';
  lastAction: string | null;
  lastActivityAt: string | number | null;
  notes: string | null;
  crmSync: boolean | null;
  crmTarget: 'hubspot' | 'pipedrive' | 'salesforce' | 'csv' | 'webhook' | null;
  crmExternalId: string | null;
  crmSyncedAt: string | number | null;
  offTarget: boolean | null;
  archivedAt: string | number | null;
  createdAt: string | number;
  updatedAt: string | number;
}

export interface LeadsListFilter {
  account?: string;
  business?: string;
  status?: string;
  q?: string;
  includeArchived?: boolean;
}

function leadsListPath(
  filter: LeadsListFilter = {},
  pagination: { cursor?: string | null; limit?: number } = {},
): string {
  const params = new URLSearchParams();
  if (filter.account && filter.account !== 'all') params.set('account', filter.account);
  if (filter.business && filter.business !== 'all') params.set('business', filter.business);
  if (filter.status && filter.status !== 'all') params.set('status', filter.status);
  if (filter.q) params.set('q', filter.q);
  if (filter.includeArchived) params.set('includeArchived', '1');
  if (pagination.limit) params.set('limit', String(pagination.limit));
  if (pagination.cursor) params.set('cursor', pagination.cursor);
  const qs = params.toString();
  return qs ? `/api/v1/leads?${qs}` : '/api/v1/leads';
}

/** Paginated response shape for `GET /api/v1/leads` — Wave D11 (US-139). */
export interface LeadsListResponse {
  /** Wave D11+ canonical page-payload field. */
  items: LeadRow[];
  /** Back-compat alias — same array as `items`. */
  leads: LeadRow[];
  nextCursor: string | null;
}

/**
 * `GET /api/v1/leads` — list (defaults to non-archived).
 *
 * Wave D11 (US-139): the route now returns `{ items, nextCursor }`. The
 * `leads` field is preserved as an alias so existing consumers reading
 * `data.leads` keep compiling. New consumers should read `data.items` /
 * fan-out via `useInfiniteLeads`.
 */
export function useLeads(
  filter: LeadsListFilter = {},
  options: { enabled?: boolean; limit?: number } = {},
): UseQueryResult<LeadsListResponse, ApiError> {
  const client = useApiClient();
  return useQuery<LeadsListResponse, ApiError>({
    queryKey: ['leads', filter, options.limit ?? null],
    queryFn: async () => {
      const raw = await getJson<{
        items?: LeadRow[];
        leads?: LeadRow[];
        nextCursor?: string | null;
      }>(client, leadsListPath(filter, { limit: options.limit }));
      const items = raw.items ?? raw.leads ?? [];
      return { items, leads: items, nextCursor: raw.nextCursor ?? null };
    },
    enabled: options.enabled,
    retry: false,
  });
}

/**
 * `GET /api/v1/leads` — infinite-scroll variant (US-139). Each page is
 * `LeadsListResponse`; `getNextPageParam` reads `nextCursor`.
 */
export function useInfiniteLeads(
  filter: LeadsListFilter = {},
  options: { enabled?: boolean; limit?: number } = {},
): UseInfiniteQueryResult<InfiniteData<LeadsListResponse, string | null>, ApiError> {
  const client = useApiClient();
  const limit = options.limit ?? 50;
  return useInfiniteQuery<
    LeadsListResponse,
    ApiError,
    InfiniteData<LeadsListResponse, string | null>,
    readonly [string, LeadsListFilter, number, 'infinite'],
    string | null
  >({
    queryKey: ['leads', filter, limit, 'infinite'] as const,
    initialPageParam: null,
    queryFn: async ({ pageParam }) => {
      const raw = await getJson<{
        items?: LeadRow[];
        leads?: LeadRow[];
        nextCursor?: string | null;
      }>(client, leadsListPath(filter, { cursor: pageParam, limit }));
      const items = raw.items ?? raw.leads ?? [];
      return { items, leads: items, nextCursor: raw.nextCursor ?? null };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: options.enabled,
    retry: false,
  });
}

/** `GET /api/v1/leads/:id` — single lead. */
export function useLead(
  id: string | undefined,
  options: { enabled?: boolean } = {},
): UseQueryResult<{ lead: LeadRow }, ApiError> {
  const client = useApiClient();
  const enabled = options.enabled ?? Boolean(id);
  return useQuery<{ lead: LeadRow }, ApiError>({
    queryKey: ['lead', id],
    queryFn: () =>
      getJson<{ lead: LeadRow }>(client, `/api/v1/leads/${encodeURIComponent(id ?? '')}`),
    enabled,
    retry: false,
  });
}

export interface CreateLeadParams {
  prospectName: string;
  prospectInitials: string;
  prospectTitle?: string;
  prospectCompany?: string;
  prospectLinkedinUrn?: string;
  prospectVanitySlug?: string;
  prospectAvatarUrl?: string;
  linkedinAccountId?: string;
  businessId?: string;
  validatedFrom?: 'inbox' | 'slack' | 'discord' | 'extension';
  status?: LeadRow['status'];
}

/** `POST /api/v1/leads` — extension capture (US-153). Returns
 *  `{ lead, duplicate }`; `duplicate=true` when server-side dedupe matched. */
export function useCreateLead(): UseMutationResult<
  { lead: LeadRow; duplicate: boolean },
  ApiError,
  CreateLeadParams
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ lead: LeadRow; duplicate: boolean }, ApiError, CreateLeadParams>({
    mutationFn: (vars) =>
      postJson<{ lead: LeadRow; duplicate: boolean }>(client, '/api/v1/leads', vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export interface UpdateLeadParams {
  id: string;
  patch: Partial<{
    status: LeadRow['status'];
    notes: string;
    businessId: string | null;
    lastAction: string;
  }>;
}

/* ─── Optimistic rollback contexts (Wave D11) ─────────────────────────────
 *
 * Each lead mutation snapshots the relevant query slices in `onMutate`
 * before applying the optimistic write. `onError` restores from the
 * snapshot — `onSettled` re-invalidates so the server is the source of
 * truth on settle.
 */

interface UpdateLeadCtx {
  prevDetail: { lead: LeadRow } | undefined;
  prevLists: [unknown, unknown][];
}

/**
 * Walk every `['leads', ...]` cache entry and apply `mapper` to each
 * cached `LeadRow` it carries. Handles both the `useLeads` shape
 * (`{ items, leads, nextCursor }`) and the legacy `{ leads }` shape, plus
 * the `useInfiniteLeads` paged shape.
 */
function patchLeadsListCaches(
  qc: QueryClient,
  predicate: (lead: LeadRow) => boolean,
  mapper: (lead: LeadRow) => LeadRow | null,
): [unknown, unknown][] {
  const snapshots: [unknown, unknown][] = [];
  const queries = qc.getQueriesData({ queryKey: ['leads'] });
  for (const [key, value] of queries) {
    snapshots.push([key, value]);
    if (!value || typeof value !== 'object') continue;
    const v = value as {
      items?: LeadRow[];
      leads?: LeadRow[];
      nextCursor?: string | null;
      pages?: { items?: LeadRow[]; leads?: LeadRow[]; nextCursor?: string | null }[];
      pageParams?: unknown[];
    };
    if (Array.isArray(v.pages)) {
      const pages = v.pages.map((p) => {
        const items = (p.items ?? p.leads ?? []) as LeadRow[];
        const next: LeadRow[] = [];
        for (const lead of items) {
          if (!predicate(lead)) {
            next.push(lead);
            continue;
          }
          const mapped = mapper(lead);
          if (mapped) next.push(mapped);
        }
        return { ...p, items: next, leads: next };
      });
      qc.setQueryData(key, { ...v, pages });
      continue;
    }
    const items = (v.items ?? v.leads ?? []) as LeadRow[];
    const next: LeadRow[] = [];
    for (const lead of items) {
      if (!predicate(lead)) {
        next.push(lead);
        continue;
      }
      const mapped = mapper(lead);
      if (mapped) next.push(mapped);
    }
    if ('items' in v || 'leads' in v) {
      qc.setQueryData(key, { ...v, items: next, leads: next });
    }
  }
  return snapshots;
}

/** `PATCH /api/v1/leads/:id` — used for inline status edits + business rebind. */
export function useUpdateLead(): UseMutationResult<
  { lead: LeadRow },
  ApiError,
  UpdateLeadParams,
  UpdateLeadCtx
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ lead: LeadRow }, ApiError, UpdateLeadParams, UpdateLeadCtx>({
    mutationFn: (vars) =>
      patchJson<{ lead: LeadRow }>(
        client,
        `/api/v1/leads/${encodeURIComponent(vars.id)}`,
        vars.patch,
      ),
    onMutate: async (vars) => {
      // Pause every concurrent fetch so the optimistic write isn't
      // immediately overwritten by an in-flight reply.
      await qc.cancelQueries({ queryKey: ['lead', vars.id] });
      await qc.cancelQueries({ queryKey: ['leads'] });

      const prevDetail = qc.getQueryData<{ lead: LeadRow }>(['lead', vars.id]);
      if (prevDetail?.lead) {
        qc.setQueryData(['lead', vars.id], {
          lead: { ...prevDetail.lead, ...(vars.patch as Partial<LeadRow>) },
        });
      }

      const prevLists = patchLeadsListCaches(
        qc,
        (lead) => lead.id === vars.id,
        (lead) => ({ ...lead, ...(vars.patch as Partial<LeadRow>) }),
      );

      return { prevDetail, prevLists };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.prevDetail) {
        qc.setQueryData(['lead', vars.id], ctx.prevDetail);
      }
      if (ctx?.prevLists) {
        for (const [key, value] of ctx.prevLists) {
          qc.setQueryData(key as readonly unknown[], value);
        }
      }
    },
    onSuccess: (data, vars) => {
      qc.setQueryData(['lead', vars.id], data);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

interface DeleteLeadCtx {
  prevLists: [unknown, unknown][];
  prevDetail: { lead: LeadRow } | undefined;
}

/** `DELETE /api/v1/leads/:id` — soft archive (US-147). */
export function useDeleteLead(): UseMutationResult<
  { ok: true },
  ApiError,
  { id: string },
  DeleteLeadCtx
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, { id: string }, DeleteLeadCtx>({
    mutationFn: (vars) =>
      deleteJson<{ ok: true }>(client, `/api/v1/leads/${encodeURIComponent(vars.id)}`),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ['leads'] });
      await qc.cancelQueries({ queryKey: ['lead', vars.id] });
      const prevDetail = qc.getQueryData<{ lead: LeadRow }>(['lead', vars.id]);
      // Optimistically drop the row from every list cache.
      const prevLists = patchLeadsListCaches(
        qc,
        (lead) => lead.id === vars.id,
        () => null,
      );
      return { prevLists, prevDetail };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.prevLists) {
        for (const [key, value] of ctx.prevLists) {
          qc.setQueryData(key as readonly unknown[], value);
        }
      }
      if (ctx?.prevDetail) {
        qc.setQueryData(['lead', vars.id], ctx.prevDetail);
      }
    },
    onSuccess: (_data, vars) => {
      qc.removeQueries({ queryKey: ['lead', vars.id] });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

interface BulkArchiveCtx {
  prevLists: [unknown, unknown][];
}

/** `POST /api/v1/leads/bulk-archive` — bulk archive (US-146). */
export function useBulkArchiveLeads(): UseMutationResult<
  { archived: number },
  ApiError,
  { ids: string[] },
  BulkArchiveCtx
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ archived: number }, ApiError, { ids: string[] }, BulkArchiveCtx>({
    mutationFn: (vars) =>
      postJson<{ archived: number }>(client, '/api/v1/leads/bulk-archive', vars),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ['leads'] });
      const idSet = new Set(vars.ids);
      // Mark each selected row archived (drop from non-archived view).
      const nowIso = new Date().toISOString();
      const prevLists = patchLeadsListCaches(
        qc,
        (lead) => idSet.has(lead.id),
        (lead) => ({ ...lead, archivedAt: nowIso }),
      );
      return { prevLists };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prevLists) {
        for (const [key, value] of ctx.prevLists) {
          qc.setQueryData(key as readonly unknown[], value);
        }
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

/**
 * `POST /api/v1/leads/:id/unarchive` — Wave C1 (US-146/147) undo flow. The
 * 5-second toast on archive lets the user reverse it; this resets
 * `archivedAt` to NULL.
 */
export function useUnarchiveSingleLead(): UseMutationResult<
  { ok: true },
  ApiError,
  { id: string }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, { id: string }>({
    mutationFn: (vars) =>
      postJson<{ ok: true }>(client, `/api/v1/leads/${encodeURIComponent(vars.id)}/unarchive`, {}),
    onSuccess: (_data, vars) => {
      qc.removeQueries({ queryKey: ['lead', vars.id] });
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

/** `POST /api/v1/leads/bulk-unarchive` — bulk counterpart for the undo path. */
export function useBulkUnarchiveLeads(): UseMutationResult<
  { unarchived: number },
  ApiError,
  { ids: string[] }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ unarchived: number }, ApiError, { ids: string[] }>({
    mutationFn: (vars) =>
      postJson<{ unarchived: number }>(client, '/api/v1/leads/bulk-unarchive', vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

/**
 * `POST /api/v1/conversations/:id/reopen` — Wave C1 (US-067). Clears the
 * `outcome_status` + `lead_pushed` flags so a previously-archived
 * conversation re-enters the inbox queue.
 */
export function useReopenConversation(): UseMutationResult<
  { reopened: { conversationId: string; at: string } },
  ApiError,
  { id: string }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<
    { reopened: { conversationId: string; at: string } },
    ApiError,
    { id: string }
  >({
    mutationFn: (vars) =>
      postJson<{ reopened: { conversationId: string; at: string } }>(
        client,
        `/api/v1/conversations/${encodeURIComponent(vars.id)}/reopen`,
        {},
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['conversation', vars.id] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export interface CrmExportParams {
  id: string;
  target: 'hubspot' | 'pipedrive' | 'salesforce' | 'csv' | 'webhook';
  externalId?: string;
}

/** Compatibility hook for CRM export. The API returns `crm.unavailable`
 * until a user-owned provider connection and real delivery adapter exist. */
export function useCrmExport(): UseMutationResult<never, ApiError, CrmExportParams> {
  const client = useApiClient();
  return useMutation<never, ApiError, CrmExportParams>({
    mutationFn: (vars) =>
      postJson<never>(client, `/api/v1/leads/${encodeURIComponent(vars.id)}/crm-export`, {
        target: vars.target,
        externalId: vars.externalId,
      }),
  });
}

export interface WebhookExportParams {
  id: string;
  url: string;
  secret?: string;
}

/** Compatibility hook for webhook export. The API fails closed and performs
 * no outbound request until secure webhook configuration/delivery exists. */
export function useWebhookExport(): UseMutationResult<never, ApiError, WebhookExportParams> {
  const client = useApiClient();
  return useMutation<never, ApiError, WebhookExportParams>({
    mutationFn: (vars) =>
      postJson<never>(client, `/api/v1/leads/${encodeURIComponent(vars.id)}/webhook-export`, {
        url: vars.url,
        secret: vars.secret,
      }),
  });
}

/** Backwards-compat aliases kept for typed-stub consumers. */
export const useArchiveLead = useDeleteLead;
export const useUnarchiveLead = useUnarchiveSingleLead;
export const useExportLeadToCrm = useCrmExport;

// Lead tags.
export interface LeadTagRow {
  id: string;
  userId: string;
  name: string;
  color: string;
  createdAt: string | number;
}

export interface LeadTagsResponse {
  tags: LeadTagRow[];
  assignedTagIds: string[];
}

export function useLeadTags(
  leadId?: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<LeadTagsResponse, ApiError> {
  const client = useApiClient();
  const query = leadId ? `?leadId=${encodeURIComponent(leadId)}` : '';
  return useQuery<LeadTagsResponse, ApiError>({
    queryKey: ['lead-tags', leadId ?? null],
    queryFn: () => getJson<LeadTagsResponse>(client, `/api/v1/lead-tags${query}`),
    enabled: options.enabled,
    retry: false,
  });
}

export function useCreateLeadTag(): UseMutationResult<
  { tag: LeadTagRow },
  ApiError,
  { name: string; color: string }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars) => postJson<{ tag: LeadTagRow }>(client, '/api/v1/lead-tags', vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lead-tags'] });
    },
  });
}

export function useDeleteLeadTag(): UseMutationResult<{ ok: true }, ApiError, { id: string }> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars) =>
      deleteJson<{ ok: true }>(client, `/api/v1/lead-tags/${encodeURIComponent(vars.id)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lead-tags'] });
    },
  });
}

export function useAssignLeadTag(): UseMutationResult<
  { tag: LeadTagRow },
  ApiError,
  { leadId: string; tagId: string }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars) =>
      postJson<{ tag: LeadTagRow }>(
        client,
        `/api/v1/leads/${encodeURIComponent(vars.leadId)}/tags`,
        { tagId: vars.tagId },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['lead-tags', vars.leadId] });
    },
  });
}

export function useUnassignLeadTag(): UseMutationResult<
  { ok: true },
  ApiError,
  { leadId: string; tagId: string }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars) =>
      deleteJson<{ ok: true }>(
        client,
        `/api/v1/leads/${encodeURIComponent(vars.leadId)}/tags/${encodeURIComponent(vars.tagId)}`,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['lead-tags', vars.leadId] });
    },
  });
}

// Runtime relances queue — landed in Wave 5d Agent 5d.3 (US-162..US-165).
// `usePendingRelances`, `useApproveRelance`, `useSkipRelance`, and
// `useSnoozeRelance` are exported from the implemented block below.
//
// Back-compat shims for the original placeholder names — kept so the
// scheduler-side surface stays import-stable for any caller that grabbed
// them before W5d.3 landed. New code should use the `usePending*` /
// `use*Relance` quartet directly.
export const useRelances = () => useNotImplementedQuery<unknown>('Wave 6', 'useRelances');
export const useUpdateRelance = () => useNotImplemented<unknown>('Wave 6', 'useUpdateRelance');
export const useCancelRelance = () => useNotImplemented<unknown>('Wave 6', 'useCancelRelance');
